import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { carteLiveAPI } from '../../../services/api';
import { LIVE_ARTIFACT_ID, cleNom, etatCarte, liveMapOps, resumeCarte, transitionsZones, vehiculesSuivis, versionsParOp } from './liveMapModel.js';

// Carte live de la conversation (liveMapModel.js) : état déduit des messages, version affichée,
// positions en direct (relues toutes les POSITIONS_MS tant que la carte est affichée et la page
// visible), élément sélectionné (puce du compositeur), événements des zones de surveillance
// (message dans le fil et toast sur la carte), ouverture automatique du panneau pendant une
// réponse qui produit une carte ou la modifie.

const LiveMapContext = createContext(null);
export const useLiveMap = () => useContext(LiveMapContext);
export const LiveMapProvider = LiveMapContext.Provider;

// Rythme de relecture des positions ; essais de bout en bout : __DIGITRANS_POSITIONS_MS (plus court)
export const POSITIONS_MS = (typeof window !== 'undefined' && Number(window.__DIGITRANS_POSITIONS_MS)) || 15000;
const TOASTS_MAX = 3;

const heureLocale = () => new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Abidjan' });

/** Message d'événement d'une zone de surveillance, affiché dans le fil (jamais renvoyé au modèle). */
export function messageEvenement({ genre, zone, vehicule, heure }) {
  const verbe = genre === 'entree' ? 'est entré dans' : 'est sorti de';
  return {
    id: `carte-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    role: 'assistant',
    origin: 'carte',
    content: `${vehicule} ${verbe} « ${zone} » · ${heure}`,
    evenement: { genre, zone, vehicule, heure },
    reasoning: { items: [], status: 'idle' },
    artifacts: [],
    isStreaming: false,
    outcome: 'done',
  };
}

export function useLiveMapController({ messages, userContext, appendLocalMessage, openArtifact, openId, wide, streaming }) {
  const { ops, enCours } = useMemo(() => liveMapOps(messages), [messages]);
  // Signature des opérations : l'état n'est recalculé que si une carte ou une modification arrive
  const signature = ops.map((o) => o.id).join('|');
  const opsRef = useRef(ops);
  opsRef.current = ops;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const etat = useMemo(() => etatCarte(opsRef.current), [signature]);
  const parOp = useMemo(() => versionsParOp(etat), [etat]);
  const [vue, setVue] = useState(null);
  const affiche = useMemo(() => (vue && vue < etat.version ? etatCarte(opsRef.current, vue) : etat), [vue, etat]);

  const [positions, setPositions] = useState({});
  const [luA, setLuA] = useState(null);
  const [liveErreur, setLiveErreur] = useState(false);
  const [selection, setSelection] = useState(null);
  const [toasts, setToasts] = useState([]);
  const presenceRef = useRef(null);

  // Nouvelle conversation (aucune carte) : positions, sélection et présence oubliées
  const vide = etat.version === 0;
  useEffect(() => {
    if (!vide) return;
    setPositions({});
    setLuA(null);
    setSelection(null);
    setVue(null);
    presenceRef.current = null;
  }, [vide]);

  // Événements déjà dans le fil (rechargement d'une conversation compris)
  const evenements = useMemo(
    () => messages.filter((m) => m.origin === 'carte').map((m) => ({ texte: m.content, ...m.evenement })),
    [messages]
  );

  // silencieux : ouverture automatique pendant une réponse, sans déplacer le focus
  const ouvrir = useCallback((version = null, { silencieux = false } = {}) => {
    setVue(version);
    openArtifact?.({ id: LIVE_ARTIFACT_ID, type: 'carte-live', title: 'Carte live', subtitle: null, payload: {}, silencieux });
  }, [openArtifact]);

  // Ouverture automatique : la réponse en cours prépare une carte ou en ajoute une (écran large)
  const vuRef = useRef(etat.version);
  useEffect(() => {
    const avant = vuRef.current;
    vuRef.current = etat.version;
    if (!wide || !streaming) return;
    if ((enCours || etat.version > avant) && openId !== LIVE_ARTIFACT_ID) ouvrir(null, { silencieux: true });
    else if (etat.version > avant) setVue(null);
  }, [enCours, etat.version, wide, streaming, openId, ouvrir]);

  const pousserToast = useCallback((toast) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    setToasts((list) => [...list.slice(-(TOASTS_MAX - 1)), { id, ...toast }]);
    setTimeout(() => setToasts((list) => list.filter((t) => t.id !== id)), 6000);
  }, []);

  // ── Positions en direct ──
  const vehicules = useMemo(() => vehiculesSuivis(etat), [etat]);
  const cleVehicules = vehicules.join('|');
  const actif = vehicules.length > 0 && openId === LIVE_ARTIFACT_ID && !!userContext?.entreprise_id && !!userContext?.user_api_hash;
  const zonesRef = useRef(etat.zones);
  zonesRef.current = etat.zones;
  const appendRef = useRef(appendLocalMessage);
  appendRef.current = appendLocalMessage;

  useEffect(() => {
    if (!actif) return undefined;
    let arret = false;
    let minuterie = null;
    let controleur = null;
    const lire = async () => {
      if (arret) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        minuterie = setTimeout(lire, POSITIONS_MS);
        return;
      }
      controleur = new AbortController();
      try {
        const r = await carteLiveAPI.positions(cleVehicules.split('|'), userContext, controleur.signal);
        if (arret) return;
        const recues = r?.positions && typeof r.positions === 'object' ? r.positions : {};
        setPositions((avant) => ({ ...avant, ...recues }));
        setLuA(r?.lu_a ?? new Date().toISOString());
        setLiveErreur(false);
        // Zones de surveillance : présence initiale sans événement, puis entrées et sorties
        const zones = zonesRef.current;
        if (zones.length) {
          const avant = presenceRef.current ?? Object.fromEntries(zones.map((z) => [z.id, new Set((z.dedans ?? []).map(cleNom))]));
          const { presence, evenements: evs } = transitionsZones(zones, recues, avant);
          presenceRef.current = presence;
          const noms = new Map(vehiculesNoms(opsRef.current));
          for (const ev of evs) {
            const vehicule = noms.get(ev.cle) ?? ev.cle.toUpperCase();
            const message = messageEvenement({ genre: ev.genre, zone: ev.zone.nom, vehicule, heure: heureLocale() });
            appendRef.current?.(message);
            pousserToast({ genre: ev.genre, texte: message.content, zone: ev.zone.id });
          }
        }
      } catch {
        if (!arret) setLiveErreur(true);
      }
      if (!arret) minuterie = setTimeout(lire, POSITIONS_MS);
    };
    lire();
    return () => {
      arret = true;
      clearTimeout(minuterie);
      controleur?.abort();
    };
  }, [actif, cleVehicules, userContext, pousserToast]);

  // Route entre deux positions réelles (conduite sur la carte) ; null : ligne droite
  const chemin = useCallback((de, vers) => carteLiveAPI.chemin(de, vers, userContext).then((r) => r?.chemin ?? null).catch(() => null), [userContext]);

  const selectionner = useCallback((element) => setSelection(element ? { nom: element.nom, cle: element.cle ?? cleNom(element.nom) } : null), []);

  const resume = useCallback(
    (question) => resumeCarte(etat, { positions, evenements, selection: question?.selection ?? null }),
    [etat, positions, evenements]
  );

  return useMemo(() => ({
    etat,
    affiche,
    vue,
    setVue,
    enCours,
    parOp,
    positions,
    luA,
    liveActif: actif,
    liveErreur,
    selection,
    selectionner,
    toasts,
    ouvrir,
    resume,
    chemin,
  }), [etat, affiche, vue, enCours, parOp, positions, luA, actif, liveErreur, selection, selectionner, toasts, ouvrir, resume, chemin]);
}

// Noms affichés des véhicules (clé → nom), pour le texte des événements
function vehiculesNoms(ops) {
  const etat = etatCarte(ops);
  const out = [];
  for (const c of etat.couches) for (const e of c.elements) if (e.vehicule) out.push([e.cle, e.nom]);
  return out;
}
